/**
 * BudgetROI — Phase 1 Growth Engine view.
 *
 * Per-campaign budget → spend → revenue → ROI ledger. Lets the founder
 * see exactly where marketing money went and what came back.
 *
 * Data: GET /api/marketing/budget-roi  (server-computed; no client math).
 */
import { useEffect, useState, useCallback } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IndianRupee, TrendingUp, TrendingDown, Search } from "lucide-react";
import { toast } from "sonner";

const fmtINR = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)} k`;
  return `₹${v.toFixed(0)}`;
};
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${n}%`);

function TotalTile({ label, value, hint, tone = "neutral", money = true, pct = false }) {
  const tones = {
    neutral: "bg-muted/40 border-border/60",
    primary: "bg-primary/10 border-primary/30 text-primary",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber:   "bg-amber-50 border-amber-200 text-amber-800",
    sky:     "bg-sky-50 border-sky-200 text-sky-700",
  };
  return (
    <div className={`rounded-2xl p-4 border ${tones[tone]}`} data-testid={`budget-total-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">{label}</p>
      <p className="font-display text-2xl font-extrabold mt-1">
        {pct ? fmtPct(value) : money ? fmtINR(value) : (Number(value) || 0).toLocaleString("en-IN")}
      </p>
      {hint && <p className="text-[11px] opacity-70 mt-0.5">{hint}</p>}
    </div>
  );
}

export default function BudgetROI() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("__all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/marketing/budget-roi");
      setData(d);
    } catch (err) { toast.error(fmtError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = (data?.rows || []).filter((r) => {
    if (statusFilter !== "__all" && r.status !== statusFilter) return false;
    if (!q.trim()) return true;
    const needle = q.trim().toLowerCase();
    return (r.name || "").toLowerCase().includes(needle)
      || (r.campaign_type || "").toLowerCase().includes(needle);
  });

  const totals = data?.totals || {};

  return (
    <div className="space-y-5" data-testid="budget-roi-panel">
      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <TotalTile label="Budget"   value={totals.budget}    tone="neutral" />
        <TotalTile label="Spend"    value={totals.spend}     tone="amber" />
        <TotalTile label="Remaining" value={totals.remaining} tone="sky" />
        <TotalTile label="Revenue"  value={totals.revenue}   tone="emerald" />
        <TotalTile label="Pipeline" value={totals.pipeline}  tone="primary" />
        <TotalTile label="Blended ROI" value={totals.roi}     tone={(totals.roi || 0) >= 0 ? "emerald" : "amber"} pct money={false} hint={`${totals.campaigns || 0} campaigns`} />
      </div>

      {/* Filter bar */}
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-3 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by campaign name or type…"
              className="pl-10 h-11 rounded-2xl bg-muted/40"
              data-testid="budget-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-2xl h-11 sm:w-44" data-testid="budget-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-2xl">
              <SelectItem value="__all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Campaigns ledger */}
      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-5 space-y-3">
              {[1,2,3,4].map((i) => <Skeleton key={i} className="h-12 w-full rounded-2xl" />)}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center">
              <div className="h-14 w-14 rounded-3xl bg-primary/10 text-primary mx-auto grid place-items-center mb-3">
                <IndianRupee className="h-6 w-6" />
              </div>
              <p className="font-semibold">No campaigns match the filters.</p>
              <p className="text-sm text-muted-foreground mt-1">Add budget + start/end dates to existing campaigns from the Campaigns tab.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    <th className="px-4 py-3 font-bold">Campaign</th>
                    <th className="px-2 py-3 font-bold">Type</th>
                    <th className="px-2 py-3 font-bold">Status</th>
                    <th className="px-2 py-3 font-bold text-right">Budget</th>
                    <th className="px-2 py-3 font-bold text-right">Spend</th>
                    <th className="px-2 py-3 font-bold text-right">Remaining</th>
                    <th className="px-2 py-3 font-bold text-right">Revenue</th>
                    <th className="px-2 py-3 font-bold text-right">Pipeline</th>
                    <th className="px-2 py-3 font-bold text-right">Won</th>
                    <th className="px-4 py-3 font-bold text-right">ROI</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const roiTone = r.roi === null ? "" : r.roi >= 0 ? "text-emerald-700" : "text-rose-700";
                    const RoiIcon = r.roi === null ? null : r.roi >= 0 ? TrendingUp : TrendingDown;
                    return (
                      <tr key={r.id} className="border-b last:border-0 hover:bg-muted/20" data-testid={`budget-row-${r.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-semibold">{r.name}</p>
                          {(r.start_date || r.end_date) && (
                            <p className="text-[11px] text-muted-foreground">{r.start_date || "—"} → {r.end_date || "—"}</p>
                          )}
                        </td>
                        <td className="px-2 py-3 text-xs capitalize text-muted-foreground">{(r.campaign_type || "").replace(/_/g, " ")}</td>
                        <td className="px-2 py-3"><Badge variant="outline" className="text-[10px] capitalize rounded-full">{r.status || "—"}</Badge></td>
                        <td className="px-2 py-3 text-right">{fmtINR(r.budget)}</td>
                        <td className="px-2 py-3 text-right">{fmtINR(r.spend)}</td>
                        <td className="px-2 py-3 text-right text-sky-700 font-semibold">{fmtINR(r.remaining)}</td>
                        <td className="px-2 py-3 text-right text-emerald-700 font-bold">{fmtINR(r.revenue)}</td>
                        <td className="px-2 py-3 text-right">{fmtINR(r.pipeline)}</td>
                        <td className="px-2 py-3 text-right">{r.won_deals}</td>
                        <td className={`px-4 py-3 text-right font-bold ${roiTone}`}>
                          <span className="inline-flex items-center gap-1 justify-end">
                            {RoiIcon && <RoiIcon className="h-3.5 w-3.5" />}
                            {fmtPct(r.roi)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
